/**
 * Utility functions for location-based operations
 * 
 * Updated to support finding practices within a specified distance
 * and finding the next available appointment within a distance range
 */

// Mock postcode data for demonstration purposes
// In a real application, you would use a geocoding API to get actual coordinates
const postcodeCoordinates = {
  // London postcodes
  'W1D': { lat: 51.5142, lng: -0.1319 }, // Central London
  'N4': { lat: 51.5707, lng: -0.1099 },  // North London
  'SW2': { lat: 51.4519, lng: -0.1252 }, // South London
  'E2': { lat: 51.5287, lng: -0.0687 },  // East London
  'W6': { lat: 51.4927, lng: -0.2248 },  // West London
  
  // Other UK cities
  'M3': { lat: 53.4808, lng: -2.2426 },  // Manchester
  'B4': { lat: 52.4814, lng: -1.8998 },  // Birmingham
  'L1': { lat: 53.4084, lng: -2.9916 },  // Liverpool
  'EH2': { lat: 55.9533, lng: -3.1883 }, // Edinburgh
  'G1': { lat: 55.8642, lng: -4.2518 },  // Glasgow
  
  // Surrey postcodes
  'KT17': { lat: 51.338982485515686, lng: -0.26289573933052635 }, // Epsom
  'KT18': { lat: 51.3307, lng: -0.2702 }, // Epsom Downs
  'KT19': { lat: 51.3521, lng: -0.2981 }, // Stoneleigh
  'KT20': { lat: 51.3177, lng: -0.2431 }, // Tadworth
  'KT21': { lat: 51.3132, lng: -0.3008 }, // Ashtead
  
  // South London postcodes
  'CR0': { lat: 51.3762, lng: -0.0982 }, // Croydon
  'CR2': { lat: 51.3477, lng: -0.0829 }, // South Croydon
  'CR4': { lat: 51.4031, lng: -0.1642 }, // Mitcham
  'CR5': { lat: 51.3192, lng: -0.1387 }, // Coulsdon
  'CR7': { lat: 51.3981, lng: -0.1089 }, // Thornton Heath
  'BR1': { lat: 51.4078, lng: 0.0197 },  // Bromley
  'BR2': { lat: 51.3792, lng: 0.0214 },  // Bromley Common
  'BR3': { lat: 51.4088, lng: -0.0291 }, // Beckenham
  'BR4': { lat: 51.3792, lng: -0.0163 }, // West Wickham
  'BR5': { lat: 51.3977, lng: 0.1052 },  // Orpington
  'BR6': { lat: 51.3667, lng: 0.0833 },  // Orpington
  'BR7': { lat: 51.4333, lng: 0.0667 },  // Chislehurst
  
  // Generate random coordinates for unknown postcodes instead of using a fixed default
  'DEFAULT': null
};

/**
 * Extract the outcode (first part of the postcode) from a full postcode
 * @param {string} postcode - Full postcode
 * @returns {string} Outcode
 */
export const extractOutcode = (postcode) => {
  if (!postcode) return '';
  
  // Remove spaces and convert to uppercase
  const cleanPostcode = postcode.replace(/\s+/g, '').toUpperCase();
  
  // Extract the outcode (first part of the postcode)
  // UK postcodes have formats like "SW1A 1AA" or "M1 1AA"
  // The outcode is "SW1A" or "M1"
  const match = cleanPostcode.match(/^([A-Z]{1,2}\d{1,2}[A-Z]?)/);
  
  return match ? match[1] : cleanPostcode;
};

// API key from environment variables
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

/**
 * Fetch coordinates for a postcode using Google Maps Geocoding API
 * @param {string} postcode - Postcode to get coordinates for
 * @returns {Promise<object|null>} Coordinates object with lat and lng properties, or null if not found
 */
const fetchCoordinatesFromAPI = async (postcode) => {
  try {
    console.log(`Fetching coordinates for postcode ${postcode} from Google Maps API`);
    
    // Format the postcode for the API request
    const formattedPostcode = postcode.replace(/\s+/g, '+');
    
    // Create the API URL
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${formattedPostcode},UK&key=${API_KEY}`;
    
    // Fetch the data
    const response = await fetch(url);
    const data = await response.json();
    
    // Check if the API returned results
    if (data.status === 'OK' && data.results && data.results.length > 0) {
      const location = data.results[0].geometry.location;
      console.log(`Found coordinates for postcode ${postcode} from API:`, location);
      return { lat: location.lat, lng: location.lng };
    } else {
      console.warn(`No results found for postcode ${postcode} from API. Status: ${data.status}`);
      return null;
    }
  } catch (error) {
    console.error(`Error fetching coordinates for postcode ${postcode}:`, error);
    return null;
  }
};

/**
 * Generate coordinates based on mock data for unknown postcodes
 * @returns {object} Coordinates object with lat and lng properties
 */
const generateCoordinatesFromMockData = (postcode) => {
  // Log that we're using mock data for this postcode
  console.log(`Using mock data for postcode ${postcode}`);
  
  const outcode = extractOutcode(postcode);
  
  // Check if we have coordinates for this outcode
  for (const key in postcodeCoordinates) {
    if (key !== 'DEFAULT' && outcode.startsWith(key)) {
      console.log(`Found coordinates for postcode ${postcode} (outcode: ${outcode}) in mock data`);
      return postcodeCoordinates[key];
    }
  }
  
  // If we don't have exact coordinates, generate deterministic ones based on the postcode
  console.log(`Generating deterministic coordinates for unknown postcode: ${postcode}`);
  
  // Generate coordinates within the UK (roughly centered around London)
  // with some randomness to ensure different postcodes get different coordinates
  const seed = postcode.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const random = (seed % 100) / 100; // Deterministic random number between 0 and 1 based on postcode
  
  // Base coordinates (London)
  const baseLat = 51.5074;
  const baseLng = -0.1278;
  
  // Add some randomness (within about 30km)
  const lat = baseLat + (random - 0.5) * 0.5;
  const lng = baseLng + (random - 0.5) * 0.5;
  
  return { lat, lng };
};

// Cache for storing previously fetched coordinates
const coordinatesCache = {};

/**
 * Get coordinates for a postcode
 * @param {string} postcode - Postcode to get coordinates for
 * @returns {Promise<object|null>} Coordinates object with lat and lng properties, or null if not found
 */
export const getCoordinatesForPostcode = async (postcode) => {
  if (!postcode) return null;
  
  // Clean the postcode
  const cleanPostcode = postcode.replace(/\s+/g, '').toUpperCase();
  
  // Check if we already have the coordinates in the cache
  if (coordinatesCache[cleanPostcode]) {
    console.log(`Using cached coordinates for postcode ${postcode}`);
    return coordinatesCache[cleanPostcode];
  }
  
  // Try to fetch coordinates from the API
  const apiCoordinates = await fetchCoordinatesFromAPI(cleanPostcode);
  
  if (apiCoordinates) {
    // Cache the coordinates
    coordinatesCache[cleanPostcode] = apiCoordinates;
    return apiCoordinates;
  }
  
  // If API fails, fall back to mock data
  const mockCoordinates = generateCoordinatesFromMockData(cleanPostcode);
  
  // Cache the coordinates
  coordinatesCache[cleanPostcode] = mockCoordinates;
  return mockCoordinates;
};

/**
 * Calculate the distance between two coordinates using the Haversine formula
 * @param {object} coords1 - First coordinates object with lat and lng properties
 * @param {object} coords2 - Second coordinates object with lat and lng properties
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (coords1, coords2) => {
  if (!coords1 || !coords2) return Infinity;
  
  // Convert string coordinates to numbers if needed
  const lat1 = typeof coords1.lat === 'string' ? parseFloat(coords1.lat) : coords1.lat;
  const lng1 = typeof coords1.lng === 'string' ? parseFloat(coords1.lng) : coords1.lng;
  const lat2 = typeof coords2.lat === 'string' ? parseFloat(coords2.lat) : coords2.lat;
  const lng2 = typeof coords2.lng === 'string' ? parseFloat(coords2.lng) : coords2.lng;
  
  if (isNaN(lat1) || isNaN(lng1) || isNaN(lat2) || isNaN(lng2)) return Infinity;
  
  const R = 6371; // Radius of the Earth in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c; // Distance in km
  
  return distance;
};

/**
 * Find practices within a specified distance from a given postcode
 * @param {string} postcode - User's postcode
 * @param {Array} practices - Array of practice objects
 * @param {number} maxDistance - Maximum distance in kilometers
 * @returns {Promise<Array>} Array of practice objects within the specified distance
 */
export const findPracticesWithinDistance = async (postcode, practices, maxDistance) => {
  // Ensure practices is an array
  if (!Array.isArray(practices)) {
    console.error('findPracticesWithinDistance: practices is not an array', practices);
    return [];
  }
  
  if (!postcode || practices.length === 0) return [];
  
  try {
    const userCoords = await getCoordinatesForPostcode(postcode);
    
    if (!userCoords) return [];
    
    const practicesWithDistancePromises = practices.map(async practice => {
      let distance = Infinity;
      
      // First try to use the coords field if available
      if (practice && practice.coords && practice.coords.lat && practice.coords.lng) {
        distance = calculateDistance(userCoords, practice.coords);
      } 
      // Fall back to using the address postcode if coords not available
      else if (practice && practice.address && practice.address.postcode) {
        const practiceCoords = await getCoordinatesForPostcode(practice.address.postcode);
        
        if (practiceCoords) {
          distance = calculateDistance(userCoords, practiceCoords);
        }
      }
      
      return {
        ...practice,
        distance: Math.round(distance * 10) / 10, // Round to 1 decimal place
      };
    });
    
    try {
      // Wait for all distance calculations to complete
      const practicesWithDistance = await Promise.all(practicesWithDistancePromises);
      
      // Ensure practicesWithDistance is an array
      if (!Array.isArray(practicesWithDistance)) {
        console.error('findPracticesWithinDistance: practicesWithDistance is not an array', practicesWithDistance);
        return [];
      }
      
      // Filter practices within the specified distance
      const practicesWithinDistance = practicesWithDistance.filter(practice => practice.distance <= maxDistance);
      
      // Sort by distance
      return practicesWithinDistance.sort((a, b) => a.distance - b.distance);
    } catch (error) {
      console.error('Error waiting for distance calculations:', error);
      return [];
    }
  } catch (error) {
    console.error('Error finding practices within distance:', error);
    return [];
  }
};

/**
 * Find the nearest practice to a given postcode
 * @param {string} postcode - User's postcode
 * @param {Array} practices - Array of practice objects
 * @returns {Promise<object|null>} Nearest practice object or null if no practices
 */
export const findNearestPractice = async (postcode, practices) => {
  // Ensure practices is an array
  if (!Array.isArray(practices)) {
    console.error('findNearestPractice: practices is not an array', practices);
    return null;
  }
  
  if (!postcode || practices.length === 0) return null;
  
  try {
    const userCoords = await getCoordinatesForPostcode(postcode);
    
    if (!userCoords) return practices[0]; // Fallback to first practice if no coordinates
    
    const practicesWithDistancePromises = practices.map(async practice => {
      let distance = Infinity;
      
      // First try to use the coords field if available
      if (practice && practice.coords && practice.coords.lat && practice.coords.lng) {
        distance = calculateDistance(userCoords, practice.coords);
      } 
      // Fall back to using the address postcode if coords not available
      else if (practice && practice.address && practice.address.postcode) {
        const practiceCoords = await getCoordinatesForPostcode(practice.address.postcode);
        
        if (practiceCoords) {
          distance = calculateDistance(userCoords, practiceCoords);
        }
      }
      
      return {
        ...practice,
        distance: Math.round(distance * 10) / 10, // Round to 1 decimal place
      };
    });
    
    try {
      // Wait for all distance calculations to complete
      const practicesWithDistance = await Promise.all(practicesWithDistancePromises);
      
      // Ensure practicesWithDistance is an array
      if (!Array.isArray(practicesWithDistance)) {
        console.error('findNearestPractice: practicesWithDistance is not an array', practicesWithDistance);
        return practices[0]; // Fallback to first practice if error
      }
      
      // Find the practice with the shortest distance
      const nearestPractice = practicesWithDistance.reduce((nearest, practice) => {
        if (!nearest || practice.distance < nearest.distance) {
          return practice;
        }
        return nearest;
      }, null);
      
      return nearestPractice;
    } catch (error) {
      console.error('Error waiting for distance calculations:', error);
      return practices[0]; // Fallback to first practice if error
    }
  } catch (error) {
    console.error('Error finding nearest practice:', error);
    return practices[0]; // Fallback to first practice if error
  }
};

/**
 * Find the next available appointment within a specified distance
 * @param {string} postcode - User's postcode
 * @param {Array} practices - Array of practice objects
 * @param {string} date - Date string in YYYY-MM-DD format
 * @param {Function} checkAvailabilityFn - Function to check availability for a practice
 * @param {number} maxDistance - Maximum distance in kilometers
 * @returns {Promise<{practice: object, slots: Array}>} Object containing the practice and available slots
 */
export const findNextAvailableAppointment = async (postcode, practices, date, checkAvailabilityFn, maxDistance = 20) => {
  // Ensure practices is an array
  if (!Array.isArray(practices)) {
    console.error('findNextAvailableAppointment: practices is not an array', practices);
    return { practice: null, slots: [] };
  }
  
  if (!postcode || practices.length === 0 || !date || !checkAvailabilityFn) {
    return { practice: null, slots: [] };
  }
  
  try {
    // Find practices within the specified distance
    const practicesWithinDistance = await findPracticesWithinDistance(postcode, practices, maxDistance);
    
    // Ensure practicesWithinDistance is an array
    if (!Array.isArray(practicesWithinDistance)) {
      console.error('findNextAvailableAppointment: practicesWithinDistance is not an array', practicesWithinDistance);
      return { practice: null, slots: [] };
    }
    
    // Check availability for each practice, starting with the nearest
    for (const practice of practicesWithinDistance) {
      try {
        const slots = await checkAvailabilityFn(practice.id, date);
        
        // Ensure slots is an array
        if (!Array.isArray(slots)) {
          console.error('findNextAvailableAppointment: slots is not an array', slots);
          continue;
        }
        
        if (slots.length > 0) {
          // Found a practice with available slots
          return { practice, slots };
        }
      } catch (error) {
        console.error(`Error checking availability for practice ${practice.id}:`, error);
        continue; // Continue to the next practice
      }
    }
    
    // If no appointments found within the specified distance, return empty result
    return { practice: null, slots: [] };
  } catch (error) {
    console.error('Error finding next available appointment:', error);
    return { practice: null, slots: [] };
  }
};

/**
 * Sort practices by distance from a given postcode
 * @param {string} postcode - User's postcode
 * @param {Array} practices - Array of practice objects
 * @returns {Promise<Array>} Sorted array of practice objects with distance property
 */
export const sortPracticesByDistance = async (postcode, practices) => {
  // Ensure practices is an array
  if (!Array.isArray(practices)) {
    console.error('sortPracticesByDistance: practices is not an array', practices);
    return [];
  }
  
  if (!postcode || practices.length === 0) return [];
  
  try {
    const userCoords = await getCoordinatesForPostcode(postcode);
    
    if (!userCoords) return practices; // Return unsorted if no coordinates
    
    // Calculate distance for each practice
    const practicesWithDistancePromises = practices.map(async practice => {
      // First try to use the coords field if available
      if (practice && practice.coords && practice.coords.lat && practice.coords.lng) {
        const distance = calculateDistance(userCoords, practice.coords);
        
        return {
          ...practice,
          distance: Math.round(distance * 10) / 10, // Round to 1 decimal place
        };
      } 
      // Fall back to using the address postcode if coords not available
      else if (practice && practice.address && practice.address.postcode) {
        const practiceCoords = await getCoordinatesForPostcode(practice.address.postcode);
        
        if (practiceCoords) {
          const distance = calculateDistance(userCoords, practiceCoords);
          
          return {
            ...practice,
            distance: Math.round(distance * 10) / 10, // Round to 1 decimal place
          };
        }
      }
      
      return { ...practice, distance: Infinity };
    });
    
    try {
      // Wait for all distance calculations to complete
      const practicesWithDistance = await Promise.all(practicesWithDistancePromises);
      
      // Ensure practicesWithDistance is an array
      if (!Array.isArray(practicesWithDistance)) {
        console.error('sortPracticesByDistance: practicesWithDistance is not an array', practicesWithDistance);
        return [];
      }
      
      // Sort by distance
      return practicesWithDistance.sort((a, b) => a.distance - b.distance);
    } catch (error) {
      console.error('Error waiting for distance calculations:', error);
      return [];
    }
  } catch (error) {
    console.error('Error sorting practices by distance:', error);
    return []; // Return empty array if error
  }
};
